/**
 * Canvas State Management
 *
 * Zustand store for managing React Flow canvas state.
 * Handles nodes, edges, selection, and undo/redo.
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import {
  Node,
  Edge,
  Connection,
  NodeChange,
  EdgeChange,
  applyNodeChanges,
  applyEdgeChanges,
  addEdge,
} from '@xyflow/react';
import { v4 as uuidv4 } from 'uuid';
import type { CanvasData } from './types';

// ============================================================================
// Constants
// ============================================================================

/** Maximum number of preview results to keep in memory */
const MAX_PREVIEW_RESULTS = 100;

/** Maximum size (in bytes) for a single preview result output */
const MAX_PREVIEW_OUTPUT_SIZE = 1024 * 1024; // 1MB

/** Maximum number of executed nodes to track in debug state */
const MAX_EXECUTED_NODES = 500;

/** Maximum number of node outputs to store in debug state */
const MAX_NODE_OUTPUTS = 100;

/** Maximum size (in bytes) for a single node output in debug state */
const MAX_NODE_OUTPUT_SIZE = 512 * 1024; // 512KB

// ============================================================================
// Node Types
// ============================================================================

export type CanvasNodeType =
  | 'input'
  | 'output'
  | 'llm'
  | 'tool'
  | 'condition'
  | 'loop'
  | 'agent'
  | 'memory'
  | 'router'
  | 'parallel'
  | 'merge'
  | 'delay'
  | 'transform'
  | 'validator'
  | 'note'
  | 'retriever'
  | 'orchestrator'
  | 'tool-executor'
  | 'webhook'
  | 'schedule'
  | 'error-handler'
  | 'workflow'
  // Automation nodes
  | 'approval'
  | 'event-trigger'
  | 'batch-start'
  | 'batch-end'
  | 'rate-limit'
  | 'retry-policy'
  // AI Enhancement nodes
  | 'rag-query'
  | 'memory-read'
  | 'memory-write'
  | 'vision'
  | 'transcribe'
  | 'evaluate';

// Node data interfaces
// Index signature required for React Flow v12 compatibility
export interface BaseNodeData extends Record<string, unknown> {
  label: string;
  description?: string;
}

export interface InputNodeData extends BaseNodeData {
  inputType: 'text' | 'json' | 'file';
  schema?: Record<string, unknown>;
}

export interface OutputNodeData extends BaseNodeData {
  outputType: 'text' | 'json' | 'structured';
  format?: string;
}

export interface LLMNodeData extends BaseNodeData {
  provider: 'anthropic' | 'openai' | 'google';
  model?: string;
  systemPrompt?: string;
  temperature?: number;
  maxTokens?: number;
}

export interface ToolNodeData extends BaseNodeData {
  toolName: string;
  toolConfig?: Record<string, unknown>;
  retryOnError?: boolean;
  maxRetries?: number;
}

export interface ConditionNodeData extends BaseNodeData {
  condition: string;
  trueLabel?: string;
  falseLabel?: string;
}

export interface LoopNodeData extends BaseNodeData {
  maxIterations: number;
  breakCondition?: string;
}

export interface AgentNodeData extends BaseNodeData {
  agentId?: string;
  agentType?: string;
  delegateTools?: boolean;
}

export interface MemoryNodeData extends BaseNodeData {
  memoryType: 'buffer' | 'summary' | 'vector';
  maxMessages?: number;
  contextKey?: string;
}

export interface RouterNodeData extends BaseNodeData {
  routes: Array<{
    label: string;
    condition: string;
  }>;
}

export interface ParallelNodeData extends BaseNodeData {
  maxConcurrency?: number;
  waitForAll?: boolean;
}

export interface MergeNodeData extends BaseNodeData {
  mergeStrategy: 'concat' | 'first' | 'last' | 'custom';
  customMerge?: string;
}

export interface DelayNodeData extends BaseNodeData {
  delayMs: number;
  condition?: string;
}

export interface TransformNodeData extends BaseNodeData {
  transformType: 'jq' | 'template' | 'script';
  expression: string;
}

export interface ValidatorNodeData extends BaseNodeData {
  schema: Record<string, unknown>;
  onInvalid: 'error' | 'skip' | 'default';
  defaultValue?: unknown;
}

export interface NoteNodeData extends BaseNodeData {
  content: string;
  color?: string;
}

export interface RetrieverNodeData extends BaseNodeData {
  retrieverType: 'vector' | 'keyword' | 'hybrid';
  topK: number;
  threshold?: number;
  namespace?: string;
  filters?: Record<string, unknown>;
}

export interface OrchestratorNodeData extends BaseNodeData {
  strategy: 'sequential' | 'parallel' | 'hierarchical' | 'consensus';
  agents: string[];
  maxRounds?: number;
  stopCondition?: string;
  aggregationMethod?: 'first' | 'last' | 'vote' | 'merge';
}

export interface ToolExecutorNodeData extends BaseNodeData {
  tools: string[];
  executionMode: 'sequential' | 'parallel' | 'conditional';
  retryPolicy: {
    maxRetries: number;
    backoffMs: number;
    retryOn: string[];
  };
  timeout?: number;
  fallbackTool?: string;
}

export interface WebhookNodeData extends BaseNodeData {
  webhookId?: string;
  secretToken?: string;
  inputMapping?: Record<string, string>;
  requireSignature?: boolean;
  rateLimit?: number;
}

export interface ScheduleNodeData extends BaseNodeData {
  scheduleType: 'cron' | 'interval' | 'once';
  cronExpression?: string;
  intervalMs?: number;
  runAt?: string;
  timezone?: string;
  inputTemplate?: Record<string, unknown>;
}

export interface ErrorHandlerNodeData extends BaseNodeData {
  errorTypes: string[];
  action: 'retry' | 'fallback' | 'skip' | 'throw';
  maxRetries?: number;
  retryDelayMs?: number;
  fallbackValue?: unknown;
  notifyOnError?: boolean;
}

// Automation node data interfaces
export interface ApprovalNodeData extends BaseNodeData {
  title: string;
  message?: string;
  assignees: string[];
  escalationChain?: string[];
  timeoutHours: number;
  timeoutAction: 'proceed' | 'reject' | 'escalate';
  priority: 'critical' | 'high' | 'medium' | 'low';
}

export interface EventTriggerNodeData extends BaseNodeData {
  eventType: 'database_change' | 'file_upload' | 'email_received' | 'api_call' | 'message_queue' | 'custom_webhook';
  eventSource: string;
  eventConfig?: Record<string, unknown>;
  filterExpression?: string;
  inputMapping?: Record<string, string>;
}

export interface BatchStartNodeData extends BaseNodeData {
  inputSource: 'inline' | 'file' | 'query' | 'api';
  parallelism: number;
  rateLimitPerMinute?: number;
  stopOnErrorThreshold?: number;
  retryFailedItems?: boolean;
}

export interface BatchEndNodeData extends BaseNodeData {
  outputDestination?: 'file' | 'table' | 'webhook' | 'none';
  outputConfig?: Record<string, unknown>;
  aggregationType: 'collect' | 'reduce' | 'summary';
}

export interface RateLimitNodeData extends BaseNodeData {
  resourceType: 'api' | 'workflow' | 'integration' | 'user' | 'webhook' | 'batch';
  maxRequests: number;
  windowSizeMs: number;
  burstLimit?: number;
  onLimitReached: 'wait' | 'skip' | 'error';
}

export interface RetryPolicyNodeData extends BaseNodeData {
  policyType: 'none' | 'linear' | 'exponential' | 'custom';
  maxAttempts: number;
  baseDelayMs: number;
  maxDelayMs: number;
  jitter: boolean;
  retryableErrors?: string[];
}

// AI Enhancement node data interfaces
export interface RAGQueryNodeData extends BaseNodeData {
  collectionId: string;
  queryType: 'vector' | 'hybrid' | 'multi';
  topK: number;
  threshold?: number;
  rerank?: boolean;
  rerankModel?: string;
}

export interface MemoryReadNodeData extends BaseNodeData {
  memoryType: 'episodic' | 'semantic' | 'procedural' | 'all';
  query?: string;
  limit: number;
  minImportance?: number;
}

export interface MemoryWriteNodeData extends BaseNodeData {
  memoryType: 'episodic' | 'semantic' | 'procedural';
  importance?: number;
  extractFacts?: boolean;
}

export interface VisionNodeData extends BaseNodeData {
  analysisType: 'describe' | 'ocr' | 'detect' | 'custom';
  model?: string;
  prompt?: string;
  detailed?: boolean;
}

export interface TranscribeNodeData extends BaseNodeData {
  model: 'whisper-1' | 'whisper-large-v3';
  language?: string;
  timestamps: boolean;
  speakerDiarization?: boolean;
}

export interface EvaluateNodeData extends BaseNodeData {
  testSuiteId: string;
  evalType: 'exact_match' | 'contains' | 'regex' | 'llm_judge' | 'custom';
  passingScore: number;
  rubric?: string;
}

export type CanvasNodeData =
  | InputNodeData
  | OutputNodeData
  | LLMNodeData
  | ToolNodeData
  | ConditionNodeData
  | LoopNodeData
  | AgentNodeData
  | MemoryNodeData
  | RouterNodeData
  | ParallelNodeData
  | MergeNodeData
  | DelayNodeData
  | TransformNodeData
  | ValidatorNodeData
  | NoteNodeData
  | RetrieverNodeData
  | OrchestratorNodeData
  | ToolExecutorNodeData
  | WebhookNodeData
  | ScheduleNodeData
  | ErrorHandlerNodeData
  | ApprovalNodeData
  | EventTriggerNodeData
  | BatchStartNodeData
  | BatchEndNodeData
  | RateLimitNodeData
  | RetryPolicyNodeData
  // AI Enhancement nodes
  | RAGQueryNodeData
  | MemoryReadNodeData
  | MemoryWriteNodeData
  | VisionNodeData
  | TranscribeNodeData
  | EvaluateNodeData;

// ============================================================================
// Debug and Preview Types
// ============================================================================

export type DebugStatus = 'idle' | 'paused' | 'running' | 'stepping' | 'completed' | 'error';

export interface DebugState {
  sessionId: string | null;
  status: DebugStatus;
  currentNodeId: string | null;
  variables: Record<string, unknown>;
  nodeOutputs: Record<string, unknown>;
  executedNodes: string[];
  error?: string;
}

export interface PreviewResult {
  nodeId: string;
  nodeType: string;
  input: unknown;
  output: unknown;
  outputType: {
    type: string;
    shape?: string;
  };
  error?: string;
}

export interface Breakpoint {
  nodeId: string;
  enabled: boolean;
  condition?: string;
}

// ============================================================================
// Store State
// ============================================================================

interface CanvasState {
  // Canvas data
  nodes: Node[];
  edges: Edge[];
  viewport: { x: number; y: number; zoom: number };

  // Selection
  selectedNodeIds: string[];
  selectedEdgeIds: string[];

  // UI state
  isPanelOpen: boolean;
  activeNodeId: string | null;

  // History (for undo/redo)
  history: Array<{ nodes: Node[]; edges: Edge[] }>;
  historyIndex: number;

  // Debug Mode
  breakpoints: Map<string, Breakpoint>;
  debugState: DebugState;

  // Preview Mode
  previewMode: boolean;
  sampleInput: unknown;
  previewResults: Record<string, PreviewResult>;

  // Actions
  onNodesChange: (changes: NodeChange[]) => void;
  onEdgesChange: (changes: EdgeChange[]) => void;
  onConnect: (connection: Connection) => void;

  addNode: (type: CanvasNodeType, position: { x: number; y: number }) => string;
  updateNode: (nodeId: string, data: Partial<CanvasNodeData>) => void;
  deleteNode: (nodeId: string) => void;
  duplicateNode: (nodeId: string) => void;

  addEdge: (edge: Edge) => void;
  deleteEdge: (edgeId: string) => void;

  setSelectedNodes: (nodeIds: string[]) => void;
  setSelectedEdges: (edgeIds: string[]) => void;
  clearSelection: () => void;

  setActiveNode: (nodeId: string | null) => void;
  togglePanel: (open?: boolean) => void;

  setViewport: (viewport: { x: number; y: number; zoom: number }) => void;

  // History actions
  undo: () => void;
  redo: () => void;
  saveToHistory: () => void;

  // Import/Export
  exportCanvas: () => CanvasData;
  importCanvas: (data: CanvasData) => void;
  clearCanvas: () => void;

  // Debug actions
  toggleBreakpoint: (nodeId: string) => void;
  setBreakpointCondition: (nodeId: string, condition: string | undefined) => void;
  removeBreakpoint: (nodeId: string) => void;
  clearAllBreakpoints: () => void;
  getBreakpoints: () => Breakpoint[];
  startDebug: (sessionId: string, initialNodeId: string) => void;
  updateDebugState: (updates: Partial<DebugState>) => void;
  addExecutedNode: (nodeId: string) => void;
  setNodeOutput: (nodeId: string, output: unknown) => void;
  stopDebug: () => void;

  // Preview actions
  togglePreviewMode: () => void;
  setSampleInput: (input: unknown) => void;
  setPreviewResults: (results: Record<string, PreviewResult>) => void;
  clearPreviewResults: () => void;
}

// ============================================================================
// Default Node Data
// ============================================================================

const getDefaultNodeData = (type: CanvasNodeType): CanvasNodeData => {
  switch (type) {
    case 'input':
      return { label: 'Input', inputType: 'text' };
    case 'output':
      return { label: 'Output', outputType: 'text' };
    case 'llm':
      return { label: 'LLM', provider: 'anthropic', temperature: 0.7 };
    case 'tool':
      return { label: 'Tool', toolName: '' };
    case 'condition':
      return { label: 'Condition', condition: '', trueLabel: 'Yes', falseLabel: 'No' };
    case 'loop':
      return { label: 'Loop', maxIterations: 10 };
    case 'agent':
      return { label: 'Agent', delegateTools: true };
    case 'memory':
      return { label: 'Memory', memoryType: 'buffer', maxMessages: 10 };
    case 'router':
      return { label: 'Router', routes: [] };
    case 'parallel':
      return { label: 'Parallel', maxConcurrency: 3, waitForAll: true };
    case 'merge':
      return { label: 'Merge', mergeStrategy: 'concat' };
    case 'delay':
      return { label: 'Delay', delayMs: 1000 };
    case 'transform':
      return { label: 'Transform', transformType: 'template', expression: '' };
    case 'validator':
      return { label: 'Validator', schema: {}, onInvalid: 'error' };
    case 'note':
      return { label: 'Note', content: '' };
    case 'retriever':
      return { label: 'Retriever', retrieverType: 'vector', topK: 5 };
    case 'orchestrator':
      return { label: 'Orchestrator', strategy: 'sequential', agents: [], maxRounds: 3 };
    case 'tool-executor':
      return {
        label: 'Tool Executor',
        tools: [],
        executionMode: 'sequential',
        retryPolicy: { maxRetries: 3, backoffMs: 1000, retryOn: ['timeout', 'rate_limit'] },
      };
    case 'webhook':
      return {
        label: 'Webhook Trigger',
        requireSignature: true,
        rateLimit: 60,
        inputMapping: {},
      };
    case 'schedule':
      return {
        label: 'Schedule Trigger',
        scheduleType: 'cron',
        cronExpression: '0 9 * * *',
        timezone: 'UTC',
        inputTemplate: {},
      };
    case 'error-handler':
      return {
        label: 'Error Handler',
        errorTypes: ['*'],
        action: 'retry',
        maxRetries: 3,
        retryDelayMs: 1000,
        notifyOnError: true,
      };
    case 'approval':
      return {
        label: 'Approval Gate',
        title: 'Approval Required',
        message: '',
        assignees: [],
        timeoutHours: 24,
        timeoutAction: 'reject',
        priority: 'medium',
      };
    case 'event-trigger':
      return {
        label: 'Event Trigger',
        eventType: 'custom_webhook',
        eventSource: '',
        inputMapping: {},
      };
    case 'batch-start':
      return {
        label: 'Batch Start',
        inputSource: 'inline',
        parallelism: 5,
        retryFailedItems: true,
      };
    case 'batch-end':
      return {
        label: 'Batch End',
        aggregationType: 'collect',
        outputDestination: 'none',
      };
    case 'rate-limit':
      return {
        label: 'Rate Limit',
        resourceType: 'workflow',
        maxRequests: 100,
        windowSizeMs: 60000,
        onLimitReached: 'wait',
      };
    case 'retry-policy':
      return {
        label: 'Retry Policy',
        policyType: 'exponential',
        maxAttempts: 3,
        baseDelayMs: 1000,
        maxDelayMs: 300000,
        jitter: true,
      };
    default:
      return { label: 'Node' };
  }
};

// ============================================================================
// Store
// ============================================================================

// Default debug state
const defaultDebugState: DebugState = {
  sessionId: null,
  status: 'idle',
  currentNodeId: null,
  variables: {},
  nodeOutputs: {},
  executedNodes: [],
};

export const useCanvasStore = create<CanvasState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        nodes: [],
        edges: [],
        viewport: { x: 0, y: 0, zoom: 1 },
        selectedNodeIds: [],
        selectedEdgeIds: [],
        isPanelOpen: false,
        activeNodeId: null,
        history: [],
        historyIndex: -1,

        // Debug Mode initial state
        breakpoints: new Map(),
        debugState: { ...defaultDebugState },

        // Preview Mode initial state
        previewMode: false,
        sampleInput: null,
        previewResults: {},

        // Node/Edge change handlers
        onNodesChange: (changes) => {
          set({
            nodes: applyNodeChanges(changes, get().nodes),
          });
        },

        onEdgesChange: (changes) => {
          set({
            edges: applyEdgeChanges(changes, get().edges),
          });
        },

        onConnect: (connection) => {
          set({
            edges: addEdge(
              { ...connection, id: uuidv4() },
              get().edges
            ),
          });
          get().saveToHistory();
        },

        // Node actions
        addNode: (type, position) => {
          const id = uuidv4();
          const newNode: Node = {
            id,
            type,
            position,
            data: getDefaultNodeData(type),
          };

          set({ nodes: [...get().nodes, newNode] });
          get().saveToHistory();
          return id;
        },

        updateNode: (nodeId, data) => {
          set({
            nodes: get().nodes.map((node) =>
              node.id === nodeId
                ? { ...node, data: { ...node.data, ...data } }
                : node
            ),
          });
        },

        deleteNode: (nodeId) => {
          set({
            nodes: get().nodes.filter((n) => n.id !== nodeId),
            edges: get().edges.filter(
              (e) => e.source !== nodeId && e.target !== nodeId
            ),
          });
          get().saveToHistory();
        },

        duplicateNode: (nodeId) => {
          const node = get().nodes.find((n) => n.id === nodeId);
          if (!node) return;

          const newId = uuidv4();
          const newNode: Node = {
            ...node,
            id: newId,
            position: {
              x: node.position.x + 50,
              y: node.position.y + 50,
            },
            data: { ...node.data },
          };

          set({ nodes: [...get().nodes, newNode] });
          get().saveToHistory();
        },

        // Edge actions
        addEdge: (edge) => {
          set({ edges: [...get().edges, edge] });
          get().saveToHistory();
        },

        deleteEdge: (edgeId) => {
          set({
            edges: get().edges.filter((e) => e.id !== edgeId),
          });
          get().saveToHistory();
        },

        // Selection
        setSelectedNodes: (nodeIds) => {
          set({ selectedNodeIds: nodeIds });
        },

        setSelectedEdges: (edgeIds) => {
          set({ selectedEdgeIds: edgeIds });
        },

        clearSelection: () => {
          set({ selectedNodeIds: [], selectedEdgeIds: [] });
        },

        // Panel
        setActiveNode: (nodeId) => {
          set({ activeNodeId: nodeId, isPanelOpen: nodeId !== null });
        },

        togglePanel: (open) => {
          set({ isPanelOpen: open ?? !get().isPanelOpen });
        },

        // Viewport
        setViewport: (viewport) => {
          set({ viewport });
        },

        // History
        saveToHistory: () => {
          const { nodes, edges, history, historyIndex } = get();
          const newHistory = history.slice(0, historyIndex + 1);
          newHistory.push({ nodes: [...nodes], edges: [...edges] });

          // Limit history size
          if (newHistory.length > 50) {
            newHistory.shift();
          }

          set({
            history: newHistory,
            historyIndex: newHistory.length - 1,
          });
        },

        undo: () => {
          const { history, historyIndex } = get();
          if (historyIndex > 0) {
            const prevState = history[historyIndex - 1];
            set({
              nodes: prevState.nodes,
              edges: prevState.edges,
              historyIndex: historyIndex - 1,
            });
          }
        },

        redo: () => {
          const { history, historyIndex } = get();
          if (historyIndex < history.length - 1) {
            const nextState = history[historyIndex + 1];
            set({
              nodes: nextState.nodes,
              edges: nextState.edges,
              historyIndex: historyIndex + 1,
            });
          }
        },

        // Import/Export
        exportCanvas: () => {
          const { nodes, edges, viewport } = get();
          return {
            nodes: nodes.map((n) => ({
              id: n.id,
              type: n.type || 'default',
              position: n.position,
              data: n.data as Record<string, unknown>,
            })),
            edges: edges.map((e) => ({
              id: e.id,
              source: e.source,
              target: e.target,
              sourceHandle: e.sourceHandle || undefined,
              targetHandle: e.targetHandle || undefined,
            })),
            viewport,
          };
        },

        importCanvas: (data) => {
          set({
            nodes: data.nodes.map((n) => ({
              id: n.id,
              type: n.type,
              position: n.position,
              data: n.data,
            })),
            edges: data.edges.map((e) => ({
              id: e.id,
              source: e.source,
              target: e.target,
              sourceHandle: e.sourceHandle,
              targetHandle: e.targetHandle,
            })),
            viewport: data.viewport || { x: 0, y: 0, zoom: 1 },
          });
          get().saveToHistory();
        },

        clearCanvas: () => {
          set({
            nodes: [],
            edges: [],
            selectedNodeIds: [],
            selectedEdgeIds: [],
            activeNodeId: null,
          });
          get().saveToHistory();
        },

        // Debug actions
        toggleBreakpoint: (nodeId) => {
          const breakpoints = new Map(get().breakpoints);
          if (breakpoints.has(nodeId)) {
            const bp = breakpoints.get(nodeId)!;
            bp.enabled = !bp.enabled;
            breakpoints.set(nodeId, bp);
          } else {
            breakpoints.set(nodeId, { nodeId, enabled: true });
          }
          set({ breakpoints });
        },

        setBreakpointCondition: (nodeId, condition) => {
          const breakpoints = new Map(get().breakpoints);
          const bp = breakpoints.get(nodeId);
          if (bp) {
            bp.condition = condition;
            breakpoints.set(nodeId, bp);
            set({ breakpoints });
          }
        },

        removeBreakpoint: (nodeId) => {
          const breakpoints = new Map(get().breakpoints);
          breakpoints.delete(nodeId);
          set({ breakpoints });
        },

        clearAllBreakpoints: () => {
          set({ breakpoints: new Map() });
        },

        getBreakpoints: () => {
          return Array.from(get().breakpoints.values());
        },

        startDebug: (sessionId, initialNodeId) => {
          set({
            debugState: {
              sessionId,
              status: 'paused',
              currentNodeId: initialNodeId,
              variables: {},
              nodeOutputs: {},
              executedNodes: [],
            },
          });
        },

        updateDebugState: (updates) => {
          set({
            debugState: {
              ...get().debugState,
              ...updates,
            },
          });
        },

        addExecutedNode: (nodeId) => {
          const { debugState } = get();
          if (!debugState.executedNodes.includes(nodeId)) {
            // Limit the number of tracked executed nodes
            let newExecutedNodes = [...debugState.executedNodes, nodeId];
            if (newExecutedNodes.length > MAX_EXECUTED_NODES) {
              // Keep only the most recent nodes
              newExecutedNodes = newExecutedNodes.slice(-MAX_EXECUTED_NODES);
            }
            set({
              debugState: {
                ...debugState,
                executedNodes: newExecutedNodes,
              },
            });
          }
        },

        setNodeOutput: (nodeId, output) => {
          const { debugState } = get();

          // Truncate large outputs
          let processedOutput = output;
          try {
            const outputStr = typeof output === 'string' ? output : JSON.stringify(output);
            if (outputStr && outputStr.length > MAX_NODE_OUTPUT_SIZE) {
              processedOutput = {
                _truncated: true,
                _originalSize: outputStr.length,
                preview: outputStr.slice(0, 10000) + '... [truncated]',
              };
            }
          } catch {
            // If serialization fails, keep original
          }

          // Limit the number of stored outputs
          let newNodeOutputs = {
            ...debugState.nodeOutputs,
            [nodeId]: processedOutput,
          };

          const outputKeys = Object.keys(newNodeOutputs);
          if (outputKeys.length > MAX_NODE_OUTPUTS) {
            // Remove oldest entries (keep most recent)
            const keysToRemove = outputKeys.slice(0, outputKeys.length - MAX_NODE_OUTPUTS);
            for (const key of keysToRemove) {
              delete newNodeOutputs[key];
            }
          }

          set({
            debugState: {
              ...debugState,
              nodeOutputs: newNodeOutputs,
            },
          });
        },

        stopDebug: () => {
          set({
            debugState: { ...defaultDebugState },
          });
        },

        // Preview actions
        togglePreviewMode: () => {
          set({ previewMode: !get().previewMode });
        },

        setSampleInput: (input) => {
          set({ sampleInput: input });
        },

        setPreviewResults: (results) => {
          // Limit the number of results and truncate large outputs
          const entries = Object.entries(results);

          // If we exceed MAX_PREVIEW_RESULTS, keep only the most recent
          const limitedEntries = entries.length > MAX_PREVIEW_RESULTS
            ? entries.slice(-MAX_PREVIEW_RESULTS)
            : entries;

          // Truncate large outputs to prevent memory issues
          const processedResults: Record<string, PreviewResult> = {};
          for (const [nodeId, result] of limitedEntries) {
            let processedOutput = result.output;

            // Check output size and truncate if necessary
            try {
              const outputStr = typeof result.output === 'string'
                ? result.output
                : JSON.stringify(result.output);

              if (outputStr && outputStr.length > MAX_PREVIEW_OUTPUT_SIZE) {
                processedOutput = {
                  _truncated: true,
                  _originalSize: outputStr.length,
                  preview: outputStr.slice(0, 10000) + '... [truncated]',
                };
              }
            } catch {
              // If serialization fails, keep original
            }

            processedResults[nodeId] = {
              ...result,
              output: processedOutput,
            };
          }

          set({ previewResults: processedResults });
        },

        clearPreviewResults: () => {
          set({ previewResults: {} });
        },
      }),
      {
        name: 'canvas-storage',
        partialize: (state) => ({
          nodes: state.nodes,
          edges: state.edges,
          viewport: state.viewport,
        }),
      }
    )
  )
);

export default useCanvasStore;
